import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { Trash2, Plus, DoorClosed } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only card on Preferences: manage office closure date ranges for the
 * active organisation (e.g. Christmas shutdown). Each row is
 * `{ start_date, end_date, name }`. Overlap with Public Holidays is fine —
 * the report calc unions the two sets and dedupes on date.
 */
export function OfficeClosuresCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingStart, setAddingStart] = useState("");
  const [addingEnd, setAddingEnd] = useState("");
  const [addingName, setAddingName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/office-closures");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!addingStart || !addingEnd || !addingName.trim()) return;
    if (addingEnd < addingStart) {
      toast.error("End date must be on or after the start date.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/office-closures", {
        start_date: addingStart,
        end_date: addingEnd,
        name: addingName.trim(),
      });
      setAddingStart("");
      setAddingEnd("");
      setAddingName("");
      toast.success("Office closure added");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Remove "${it.name}" (${it.start_date} → ${it.end_date})?`)) return;
    try {
      await api.delete(`/office-closures/${it.id}`);
      toast.success("Office closure removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = items.filter((i) => (i.end_date || i.start_date) >= today);
  const past = items.filter((i) => (i.end_date || i.start_date) < today);

  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-xl p-6 space-y-5" data-testid="office-closures-card">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-[#fff4e5] text-[#b45309] shrink-0">
          <DoorClosed className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-slate-700" style={{ fontFamily: "Manrope" }}>
            Office closures
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Multi-day closures (e.g. Christmas shutdown). They&apos;re shown on the calendar and
            excluded from the informational &quot;working days in range&quot; count on Reports.
            Overlap with Public Holidays is fine — the overlap won&apos;t be double-counted.
          </p>
        </div>
      </div>

      {/* Add form */}
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,160px)_minmax(0,160px)_1fr_auto] gap-2 items-end pt-1">
        <div>
          <Label className="text-xs text-slate-500">From</Label>
          <DatePicker
            value={addingStart}
            onChange={setAddingStart}
            className="h-9 mt-1 w-full"
            testId="oc-add-start"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">To</Label>
          <DatePicker
            value={addingEnd}
            onChange={setAddingEnd}
            className="h-9 mt-1 w-full"
            testId="oc-add-end"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Name</Label>
          <Input
            value={addingName}
            onChange={(e) => setAddingName(e.target.value)}
            placeholder="e.g. Xmas shutdown"
            className="h-9 mt-1"
            data-testid="oc-add-name"
          />
        </div>
        <Button
          onClick={add}
          disabled={saving || !addingStart || !addingEnd || !addingName.trim()}
          className="h-9 text-white"
          style={{ background: "#145c8c" }}
          data-testid="oc-add-button"
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-4 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg">
          No office closures yet.
        </div>
      ) : (
        <div className="space-y-4">
          {upcoming.length > 0 && <OcSection title="Upcoming" items={upcoming} onDelete={remove} />}
          {past.length > 0 && <OcSection title="Past" items={past} onDelete={remove} muted />}
        </div>
      )}
    </div>
  );
}

function OcSection({ title, items, onDelete, muted }) {
  const dayCount = (it) => {
    try {
      const s = new Date(it.start_date);
      const e = new Date(it.end_date);
      return Math.round((e - s) / 86400000) + 1;
    } catch {
      return "?";
    }
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">{title}</div>
      <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
        {items.map((it) => (
          <li
            key={it.id}
            className={`flex items-center justify-between px-3 py-2 text-sm ${muted ? "text-slate-400 bg-slate-50" : "text-slate-700 bg-white"}`}
            data-testid={`oc-row-${it.id}`}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <span className="font-mono text-xs w-48 shrink-0">
                {it.start_date} → {it.end_date}
              </span>
              <span className="truncate flex-1">{it.name}</span>
              <span className="text-[10px] uppercase tracking-widest text-slate-400 shrink-0">
                {dayCount(it)}d
              </span>
            </div>
            <button
              onClick={() => onDelete(it)}
              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded ml-2"
              data-testid={`oc-delete-${it.id}`}
              aria-label="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
